
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Shuffle, Star, BookOpen, Lightbulb, Heart, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { validateTopicContent } from "@/utils/contentValidation";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface TopicSelectorProps {
  selectedGrade: string;
  onTopicSelect: (topic: string, subject: string, writingType: string, grade: string) => void;
}

const WRITING_TOPICS = {
  'Creative': [
    'Write a story about a magical pet that can talk',
    'Describe your dream treehouse and all its amazing features',
    'What would happen if you could fly for one day?',
    'Write about a friendly monster who lives under your bed',
    'Create a story where you discover a secret door in your house'
  ],
  'Opinion': [
    'What is the best season and why?',
    'Should kids have homework on weekends?',
    'What makes a good friend?',
    'Which superpower would be most helpful?',
    'What is the most important rule at school?'
  ],
  'Informative': [
    'Explain how to take care of a pet',
    'Describe your favorite hobby step by step',
    'Write about an interesting animal and its habitat',
    'Explain how to make your favorite snack',
    'Describe what happens during your favorite season'
  ],
  'Narrative': [
    'Write about your best day ever',
    'Tell the story of learning something new',
    'Describe a time when you helped someone',
    'Write about a fun family tradition',
    'Tell about an adventure you went on'
  ]
};

const SUBJECTS = [
  'General Writing',
  'Science',
  'Social Studies',
  'Creative Writing',
  'Language Arts'
];

const TopicSelector = ({ selectedGrade, onTopicSelect }: TopicSelectorProps) => {
  const [currentGrade, setCurrentGrade] = useState(selectedGrade);
  const [selectedSubject, setSelectedSubject] = useState('General Writing');
  const [selectedWritingType, setSelectedWritingType] = useState('Creative');
  const [customTopic, setCustomTopic] = useState('');
  const [useCustomTopic, setUseCustomTopic] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleRandomTopic = () => {
    const topics = WRITING_TOPICS[selectedWritingType as keyof typeof WRITING_TOPICS];
    const randomTopic = topics[Math.floor(Math.random() * topics.length)];
    setSelectedTopic(randomTopic);
    setUseCustomTopic(false);
    setCustomTopic('');
    setValidationError('');
  };

  const handleCustomTopicChange = (value: string) => {
    setCustomTopic(value);
    setUseCustomTopic(true);
    setSelectedTopic('');
    
    // Clear previous validation errors
    setValidationError('');
    
    // Validate in real-time if there's content
    if (value.trim()) {
      const validation = validateTopicContent(value);
      if (!validation.isValid) {
        setValidationError(validation.message || '');
      }
    }
  };

  const handleSubmit = () => {
    const finalTopic = useCustomTopic ? customTopic : selectedTopic;
    
    if (!finalTopic.trim()) {
      setValidationError('Please select a topic or enter your own');
      return;
    }

    // Validate the final topic before submitting
    if (useCustomTopic) {
      const validation = validateTopicContent(customTopic);
      if (!validation.isValid) {
        setValidationError(validation.message || '');
        return;
      }
    }

    setValidationError('');
    onTopicSelect(finalTopic, selectedSubject, selectedWritingType, currentGrade);
  };

  const gradeOptions = Array.from({ length: 9 }, (_, i) => (i + 1).toString());

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-4xl mx-auto"
    >
      <Card className="border-4 border-dashed border-purple-200 bg-gradient-to-br from-white to-purple-50">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-purple-700 flex items-center justify-center gap-2">
            <Star className="h-6 w-6" />
            Choose Your Writing Adventure!
            <Star className="h-6 w-6" />
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Grade and Subject Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="grade" className="text-purple-700 font-medium">Grade Level</Label>
              <Select value={currentGrade} onValueChange={setCurrentGrade}>
                <SelectTrigger className="border-2 border-purple-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {gradeOptions.map(grade => (
                    <SelectItem key={grade} value={grade}>Grade {grade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject" className="text-purple-700 font-medium">Subject Area</Label>
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger className="border-2 border-purple-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBJECTS.map(subject => (
                    <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type" className="text-purple-700 font-medium">Writing Type</Label>
              <Select value={selectedWritingType} onValueChange={setSelectedWritingType}>
                <SelectTrigger className="border-2 border-purple-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(WRITING_TOPICS).map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Topic Selection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-purple-700 font-medium text-lg">Pick Your Topic</Label>
              <Button
                onClick={handleRandomTopic}
                variant="outline"
                size="sm"
                className="border-2 border-yellow-300 hover:bg-yellow-50"
              >
                <Shuffle className="h-4 w-4 mr-2" />
                Surprise Me!
              </Button>
            </div>

            {/* Suggested Topics */}
            <div className="grid grid-cols-1 gap-3">
              {WRITING_TOPICS[selectedWritingType as keyof typeof WRITING_TOPICS].map((topic, index) => (
                <motion.div
                  key={topic}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Button
                    variant={selectedTopic === topic && !useCustomTopic ? "default" : "outline"}
                    className={`w-full h-auto p-4 text-left justify-start hover:scale-105 transition-all duration-200 ${
                      selectedTopic === topic && !useCustomTopic 
                        ? 'bg-purple-500 hover:bg-purple-600 text-white' 
                        : 'border-2 border-purple-200 hover:border-purple-300'
                    }`}
                    onClick={() => {
                      setSelectedTopic(topic);
                      setUseCustomTopic(false);
                      setCustomTopic('');
                      setValidationError('');
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <Heart className="h-5 w-5 mt-1 flex-shrink-0" />
                      <span className="text-sm leading-relaxed">{topic}</span>
                    </div>
                  </Button>
                </motion.div>
              ))}
            </div>

            {/* Custom Topic */}
            <div className="space-y-2">
              <Label className="text-purple-700 font-medium">Or Write Your Own Topic</Label>
              <Textarea
                placeholder="What would you like to write about today?"
                value={customTopic}
                onChange={(e) => handleCustomTopicChange(e.target.value)}
                className={`border-2 min-h-20 ${validationError ? 'border-red-300' : 'border-purple-200'}`}
              />
              {validationError && (
                <Alert variant="destructive" className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{validationError}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={handleSubmit}
              disabled={(!selectedTopic && !customTopic.trim()) || !!validationError}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-3 px-8 rounded-xl text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BookOpen className="h-5 w-5 mr-2" />
              Start Writing!
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TopicSelector;
